import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { EventType, InteractionStatus, BrowserAuthError } from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { msalInstance, msalReady, loginRequest } from './config/auth';
import App from './App';
import { ThemeProvider } from './contexts/ThemeProvider';
import './styles/index.css';

function LoginGate({ children }: { children: React.ReactNode }) {
  const { accounts, inProgress } = useMsal();
  const [loginAttempted, setLoginAttempted] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (inProgress === InteractionStatus.None && accounts.length === 0 && !loginAttempted) {
      setLoginAttempted(true);
      msalInstance.loginRedirect(loginRequest).catch((err) => {
        if (err instanceof BrowserAuthError && err.errorCode === 'interaction_in_progress') {
          return;
        }
        console.warn('Login redirect failed — continuing without auth:', err);
        setTimedOut(true);
      });
    }
  }, [accounts, inProgress, loginAttempted]);

  useEffect(() => {
    if (accounts.length === 0 && !timedOut) {
      const t = setTimeout(() => setTimedOut(true), 3000);
      return () => clearTimeout(t);
    }
  }, [accounts, timedOut]);

  if (inProgress !== InteractionStatus.None && !timedOut) {
    return (
      <div className="flex items-center justify-center h-screen bg-tower-bg">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-tower-accent border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-tower-muted text-sm">Signing in...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function MsalBootstrap({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    msalReady.then(async () => {
      try {
        const response = await msalInstance.handleRedirectPromise();
        if (response?.account) {
          msalInstance.setActiveAccount(response.account);
        }
      } catch (err) {
        console.warn('handleRedirectPromise error:', err);
      }

      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(accounts[0]);
      }

      msalInstance.addEventCallback((event) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const payload = event.payload as { account?: any };
          if (payload.account) {
            msalInstance.setActiveAccount(payload.account);
          }
        }
      });

      setReady(true);
    });
  }, []);

  if (!ready) {
    return (
      <div className="flex items-center justify-center h-screen bg-tower-bg">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-tower-accent border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-tower-muted text-sm">Initializing Maestro...</p>
        </div>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <LoginGate>{children}</LoginGate>
    </MsalProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider defaultMode="light">
      <MsalBootstrap>
        <App />
      </MsalBootstrap>
    </ThemeProvider>
  </React.StrictMode>,
);
